import { useEffect } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { isUcanApp } from '@/lib/ucanApp';

const STORAGE_KEY = 'ucan_pending_apple_purchase';

const processingTransactions = new Set();
const validatedTransactions = new Set();
const blockedTransactions = new Set();

const sendNativeMessage = (message) => {
  try {
    window.ReactNativeWebView?.postMessage(
      JSON.stringify(message)
    );
  } catch (error) {
    console.error('[U-CAN APPLE] Error nativo:', error);
  }
};

const savePendingPurchase = (purchase) => {
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(purchase)
    );
  } catch (error) {
    console.error(
      '[U-CAN APPLE] Error guardando compra:',
      error
    );
  }
};

const getPendingPurchase = () => {
  try {
    return JSON.parse(
      sessionStorage.getItem(STORAGE_KEY) || 'null'
    );
  } catch {
    return null;
  }
};

const clearPendingPurchase = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Sin acción.
  }

  window.__UCAN_PENDING_APPLE_PURCHASE__ = null;
};

const ApplePurchaseListener = () => {
  useEffect(() => {
    if (!isUcanApp()) return;

    let mounted = true;
    let checkingSession = false;

    const isAuthenticated = async () => {
      try {
        const response = await api.get('/auth/me');

        return (
          response.status === 200 &&
          Boolean(response.data)
        );
      } catch {
        return false;
      }
    };

    const handlePurchase = async (event) => {
      const transactionId = String(
        event.detail?.transactionId || ''
      ).trim();

      const productId = String(
        event.detail?.productId || ''
      ).trim();

      if (!transactionId || !mounted) return;

      const purchase = {
        transactionId,
        productId,
      };

      savePendingPurchase(purchase);

      if (validatedTransactions.has(transactionId)) {
        clearPendingPurchase();

        sendNativeMessage({
          type: 'APPLE_SUBSCRIPTION_ACTIVATED',
          transactionId,
        });

        return;
      }

      // Una transacción bloqueada no debe generar
      // nuevas solicitudes ni alertas repetidas.
      if (blockedTransactions.has(transactionId)) {
        return;
      }

      if (processingTransactions.has(transactionId)) {
        return;
      }

      // Reservar la transacción antes de cualquier await.
      processingTransactions.add(transactionId);

      try {
        const authenticated = await isAuthenticated();

        if (!authenticated || !mounted) {
          console.log(
            '[U-CAN APPLE] Esperando inicio de sesión'
          );
          return;
        }

        console.log(
          '[U-CAN APPLE] Validando transacción:',
          transactionId
        );

        const response = await api.post(
          '/subscription/apple',
          {
            transaction_id: transactionId,
          }
        );

        if (!mounted) return;

        if (
          response.data?.success === true &&
          response.data?.status === 'active'
        ) {
          validatedTransactions.add(transactionId);
          clearPendingPurchase();

          sendNativeMessage({
            type: 'APPLE_SUBSCRIPTION_ACTIVATED',
            transactionId,
          });

          toast.success(
            '¡U-CAN Premium activado correctamente!'
          );

          setTimeout(() => {
            if (mounted) {
              window.location.reload();
            }
          }, 1200);

          return;
        }

        // Respuesta inesperada: conservar compra,
        // pero no repetir indefinidamente.
        blockedTransactions.add(transactionId);

        const message =
          'El servidor no confirmó Premium activo. Intenta recuperar tu compra más tarde.';

        sendNativeMessage({
          type: 'APPLE_SUBSCRIPTION_ERROR',
          transactionId,
          message,
        });

        toast.error(message);

      } catch (error) {
        if (!mounted) return;

        const status = error.response?.status;

        if (status === 401) {
          console.log(
            '[U-CAN APPLE] Sesión pendiente. Compra conservada.'
          );
          return;
        }

        const detail = error.response?.data?.detail;

        const message =
          typeof detail === 'string'
            ? detail
            : 'No pudimos validar la compra con Apple';

        // Detener reintentos automáticos después
        // de cualquier error de validación.
        // La compra permanece guardada.
        blockedTransactions.add(transactionId);

        console.error(
          '[U-CAN APPLE] Validación detenida:',
          status,
          message
        );

        sendNativeMessage({
          type: 'APPLE_SUBSCRIPTION_ERROR',
          transactionId,
          message,
        });

        toast.error(message);

      } finally {
        processingTransactions.delete(transactionId);
      }
    };

    const retryPendingPurchase = async () => {
      if (!mounted || checkingSession) return;

      const pending = getPendingPurchase();

      if (!pending?.transactionId) return;

      if (
        blockedTransactions.has(pending.transactionId) ||
        processingTransactions.has(pending.transactionId) ||
        validatedTransactions.has(pending.transactionId)
      ) {
        return;
      }

      checkingSession = true;

      try {
        await handlePurchase({
          detail: pending,
        });
      } finally {
        checkingSession = false;
      }
    };

    window.addEventListener(
      'ucan:apple-purchase',
      handlePurchase
    );

    window.__UCAN_APPLE_BRIDGE_READY__ = true;

    sendNativeMessage({
      type: 'APPLE_BRIDGE_READY',
    });

    const pending =
      window.__UCAN_PENDING_APPLE_PURCHASE__ ||
      getPendingPurchase();

    if (pending?.transactionId) {
      handlePurchase({
        detail: pending,
      });
    }

    // Solo reintenta cuando falta autenticación.
    // Las transacciones bloqueadas no se reenvían.
    const interval = setInterval(
      retryPendingPurchase,
      3000
    );

    return () => {
      mounted = false;

      clearInterval(interval);

      window.__UCAN_APPLE_BRIDGE_READY__ = false;

      window.removeEventListener(
        'ucan:apple-purchase',
        handlePurchase
      );
    };
  }, []);

  return null;
};

export default ApplePurchaseListener;
